import i18next from "i18next";
import { observable, makeObservable, action, runInAction, computed } from "mobx";
import {
    GetAttendanceByRealestateId,
    GetAttendanceByRealestateIdVariables
} from "src/api/generated/GetAttendanceByRealestateId";
import { GET_ATTENDANCE_BY_REALESTATEID } from "src/api/assembly";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { RootStore } from "src/stores/RootStore";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { MessageType } from "src/components/notifications/Notifier";
import { AssemblyRealestate } from "./AssemblyStore";
import { format } from "date-fns-tz";

export enum ATTENDANCE_STATE {
    PRESENT = "present",
    REPESENTED = "repesented",
    ABSENT = "absent"
}

export interface IAssemblyAttendances {
    tenantid: string;
    tenantname: string | null;
    attendancestate: IAttendanceState;
    unitname: string | null;
    value: string | null;
    realestateid: string | null;
    isFirstTenant: boolean;
}

export enum IAttendanceState {
    PRESENT = 0,
    ABSENT = 5,
    REPRESENTED = 1
}

export interface IAttendancesStates {
    id: string;
    key: IAttendanceState;
    label: string;
}

export interface IUpdatedTenantStates {
    tenantid: string;
    state: IAttendanceState;
}

export interface IAttendanceQuorumHeadInfo {
    presenceCounter: number;
    absenceCounter: number;
    totalCounter: number;
    presenceLabel: string;
    isHeadQuorumSatisfied: boolean;
}

export interface IAttendanceQuorumValueQuotaInfo {
    totalValueQuota: number;
    presentValueQuota: number;
    absenceValueQuota: number;
    valueQuotaLabel: string;
    isValueQuotaSatisfied: boolean;
}

export class AttendanceListStore {
    rootStore: RootStore;
    assemblyAttendances: IAssemblyAttendances[] = [];
    attendanceStates: IAttendancesStates[] = [];
    originalAssemblyAttendances: IAssemblyAttendances[] = [];
    selectedRealestateId: string = "";
    updatedTenantStates: IUpdatedTenantStates[] = [];
    attendanceFileId: string = "";
    isAttendancePDFDownloading: boolean = false;
    searchQuery: string = "";

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            assemblyAttendances: observable,
            attendanceStates: observable,
            originalAssemblyAttendances: observable,
            selectedRealestateId: observable,
            updatedTenantStates: observable,
            attendanceFileId: observable,
            isAttendancePDFDownloading: observable,
            searchQuery: observable,

            filteredAssemblyAttendances: computed,
            hasCurrentAssemblyAttendancesChanged: computed,
            quorumHeadInfo: computed,
            quorumValueQuotaInfo: computed,
            selectedCurrentAssemblyRealestate: computed,
            itemsPerPage: computed,

            updateAttendanceState: action,
            resetAttendanceList: action,
            saveAttendanceList: action,
            setAssemblyAttendances: action,
            loadAttendanceByRealestateId: action,
            setSelectedRealestateId: action,
            getItemPerPage: action,
            generateDownloadAttendanceList: action,
            generateAttendanceListPdf: action,
            downloadAttendanceListPdf: action,
            setAttendanceFileId: action,
            setIsAttendancePDFDownloading: action,
            setSearchQuery: action
        });
    }

    get quorumHeadInfo(): IAttendanceQuorumHeadInfo {
        let response: IAttendanceQuorumHeadInfo = {
            presenceCounter: 0,
            absenceCounter: 0,
            totalCounter: 0,
            presenceLabel: `${0}/${0}`,
            isHeadQuorumSatisfied: false
        };

        if (this.assemblyAttendances?.length === 0) {
            return response;
        }

        const headQuotas = this.assemblyAttendances.filter(
            (value, index, self) => index === self.findIndex((i) => i.tenantid === value.tenantid)
        );

        const presentHeadQuotas = headQuotas.filter(
            (i) => i.attendancestate === IAttendanceState.PRESENT || i.attendancestate === IAttendanceState.REPRESENTED
        ).length;

        const absenceHeadQuotas = headQuotas.filter(
            (i) => i.attendancestate !== IAttendanceState.PRESENT && i.attendancestate !== IAttendanceState.REPRESENTED
        ).length;

        const totalHeadQuotas = headQuotas.length;

        response.totalCounter = totalHeadQuotas;
        response.presenceCounter = presentHeadQuotas;
        response.absenceCounter = absenceHeadQuotas;
        response.presenceLabel = `${presentHeadQuotas}/${totalHeadQuotas}`;

        if (
            (totalHeadQuotas <= 2 && presentHeadQuotas < totalHeadQuotas) || // 1. Wenn nur 1 oder 2  Kopfstimmen ingesgesamt, falls nicht alle anwesend --> false
            (totalHeadQuotas === 3 && presentHeadQuotas < 2) || // 2. Wenn 3 Kopfstimmen, falls nicht 2 anwesend sein --> false
            presentHeadQuotas < totalHeadQuotas / 2 // 3. Ab 4 Eigentümer falls nicht 50% anwesend ---> false
        ) {
            return response;
        }

        response.isHeadQuorumSatisfied = true;
        return response;
    }

    get quorumValueQuotaInfo(): IAttendanceQuorumValueQuotaInfo {
        let response = {
            isValueQuotaSatisfied: false,
            totalValueQuota: 0,
            presentValueQuota: 0,
            absenceValueQuota: 0,
            valueQuotaLabel: `${0}/${0}`
        } as IAttendanceQuorumValueQuotaInfo;

        if (this.assemblyAttendances?.length === 0) {
            return response;
        }

        const totalValueQuota = this.assemblyAttendances.reduce((acc, curr) => {
            if (curr.value) {
                return acc + parseFloat(curr.value);
            }
            return acc;
        }, 0);

        const presentValueQuota = this.assemblyAttendances
            .filter(
                (i) =>
                    i.attendancestate === IAttendanceState.PRESENT || i.attendancestate === IAttendanceState.REPRESENTED
            )
            .reduce((acc, curr) => {
                if (curr.value) {
                    return acc + parseFloat(curr.value);
                }
                return acc;
            }, 0);

        const absenceValueQuota = this.assemblyAttendances
            .filter(
                (i) =>
                    i.attendancestate !== IAttendanceState.PRESENT && i.attendancestate !== IAttendanceState.REPRESENTED
            )
            .reduce((acc, curr) => {
                if (curr.value) {
                    return acc + parseFloat(curr.value);
                }
                return acc;
            }, 0);

        response.totalValueQuota = totalValueQuota;
        response.presentValueQuota = presentValueQuota;
        response.absenceValueQuota = absenceValueQuota;
        response.valueQuotaLabel = `${presentValueQuota}/${totalValueQuota}`;

        if (presentValueQuota < totalValueQuota / 2) {
            return response;
        }

        response.isValueQuotaSatisfied = true;
        return response;
    }

    get selectedCurrentAssemblyRealestate(): AssemblyRealestate | undefined {
        return this.rootStore.assemblyStore.currentAssembly?.assemblyrealestates.find(
            (realestate) => realestate.realestate.id === this.selectedRealestateId
        );
    }

    get hasCurrentAssemblyAttendancesChanged(): boolean {
        if (this.assemblyAttendances.length !== this.originalAssemblyAttendances.length) {
            return true;
        }

        let statusChanged = false;
        for (const filteredItem of this.filteredAssemblyAttendances) {
            // Iterate through each filtered attendance
            // Assuming tenantid is unique and available in both arrays
            const originalItem = this.originalAssemblyAttendances.find(
                (item) => item.tenantid === filteredItem.tenantid
            );

            // If no matching original record is found or the attendancestate has changed
            if (!originalItem || filteredItem.attendancestate !== originalItem.attendancestate) {
                statusChanged = true;
                break;
            }
        }
        return statusChanged;
    }

    get itemsPerPage(): number {
        const maxItems = 20;
        // If no items loaded, return the default maximum
        if (!this.assemblyAttendances || this.assemblyAttendances.length === 0) {
            return maxItems;
        }

        let count = 0;
        let i = 0;

        while (i < this.assemblyAttendances.length) {
            // Get the current tenant id
            const currentTenant = this.assemblyAttendances[i].tenantid;
            let groupCount = 0;

            // Count consecutive items with the same tenant id
            while (i < this.assemblyAttendances.length && this.assemblyAttendances[i].tenantid === currentTenant) {
                groupCount++;
                i++;
            }

            // If adding the entire group would exceed our max, stop
            if (count + groupCount > maxItems) {
                break;
            }

            count += groupCount;
        }

        return count;
    }

    get filteredAssemblyAttendances(): IAssemblyAttendances[] {
        const query = this.searchQuery.trim().toLowerCase();
        return query
            ? this.assemblyAttendances.filter((a) => a.tenantname?.toLowerCase().includes(query))
            : this.assemblyAttendances;
    }

    updateAttendanceState(tenantid: string, newStateId: string) {
        const newState = this.attendanceStates.find((s) => s.id === newStateId)?.key;
        if (newState == null) return;

        // 1) immutably update the live list
        this.assemblyAttendances = this.assemblyAttendances.map((attendance) =>
            attendance.tenantid === tenantid ? { ...attendance, attendancestate: newState } : attendance
        );

        // 2) record it for save/diff logic
        const original = this.originalAssemblyAttendances.find((o) => o.tenantid === tenantid);
        if (original && original.attendancestate === newState) {
            this.updatedTenantStates = this.updatedTenantStates.filter((u) => u.tenantid !== tenantid);
        } else {
            const idx = this.updatedTenantStates.findIndex((u) => u.tenantid === tenantid);
            if (idx > -1) this.updatedTenantStates[idx].state = newState;
            else this.updatedTenantStates.push({ tenantid, state: newState });
        }
    }

    resetAttendanceList() {
        this.updatedTenantStates = [];
        this.setAssemblyAttendances(JSON.parse(JSON.stringify(this.originalAssemblyAttendances)));
    }

    async loadAttendanceByRealestateId(assemblyId: string, realestateId: string) {
        try {
            const { data }: { data: GetAttendanceByRealestateId } = await apolloClientInstance.query<
                GetAttendanceByRealestateId,
                GetAttendanceByRealestateIdVariables
            >({
                query: GET_ATTENDANCE_BY_REALESTATEID,
                fetchPolicy: "no-cache",
                variables: {
                    assemblyid: assemblyId,
                    realestateid: realestateId,
                    language: i18next.language
                }
            });

            runInAction(() => {
                const seenTenantIds = new Set<string>();
                const mappedAttendances = data.assmeblyattendance.map((attendance) => {
                    const isFirst = !seenTenantIds.has(attendance.tenantid);
                    seenTenantIds.add(attendance.tenantid);
                    let attendanceState = attendance.attendancestate || 0;
                    // apply any overrides…
                    const updatedState = this.updatedTenantStates.find(
                        (updatedTenant) => updatedTenant.tenantid === attendance.tenantid
                    );
                    if (updatedState) attendanceState = updatedState.state;
                    return {
                        tenantid: attendance.tenantid,
                        tenantname: attendance.tenantname ?? "",
                        attendancestate: attendanceState,
                        unitname: attendance.unitname ?? "",
                        value: attendance.value ?? "",
                        realestateid: attendance.realestateid ?? "",
                        isFirstTenant: isFirst
                    };
                });

                // 2. Set the live list
                this.setAssemblyAttendances(mappedAttendances);

                // 3. On first load, snapshot the “original” list for resets/dirty-checks
                if (this.originalAssemblyAttendances.length === 0) {
                    const deepClone = JSON.parse(JSON.stringify(mappedAttendances));
                    this.originalAssemblyAttendances = deepClone;
                }

                // 4. Reset any search so computed shows the full list
                this.setSearchQuery("");

                // 5. And finally load attendanceStates…
                this.attendanceStates = data.assemblyattendancesstates.map((state) => ({
                    id: state.id,
                    key: state.key ?? 0,
                    label: state.label ?? "Unknown"
                }));
            });
        } catch (error) {
            console.error(error);
        }
    }

    async saveAttendanceList(assemblyid: string) {
        const payload = {
            objects: this.updatedTenantStates.map((updated) => ({
                assemblyid,
                tenantid: updated.tenantid,
                state: updated.state
            }))
        };

        try {
            const accessToken = this.rootStore.authStore.token;
            const tokenType = this.rootStore.authStore.tokenType;
            const role = this.rootStore.authStore.user?.role;

            const response = await fetch(NetworkConfig.updateAssemblyRealestateTenantState, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `${tokenType} ${accessToken}`,
                    "x-hasura-role": getRoleKey(role)
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 200) {
                runInAction(() => {
                    this.updatedTenantStates = [];
                    this.originalAssemblyAttendances = JSON.parse(JSON.stringify(this.assemblyAttendances));
                });

                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.assemblies.attendance.save_success"),
                    MessageType.SUCCESS
                );
            } else {
                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.assemblies.attendance.failed_saving"),
                    MessageType.ERROR
                );
                console.error("Error updateAssemblyRealestateTenantState: ", response);
            }
        } catch (error) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.attendance.failed_saving"),
                MessageType.ERROR
            );
            console.error("Error while trying to call updateAssemblyRealestateTenantState: ", error);
        }
    }

    getItemPerPage(): number {
        const maxItems = 15;
        // If no items loaded, return the default maximum
        if (!this.assemblyAttendances || this.assemblyAttendances.length === 0) {
            return maxItems;
        }

        let count = 0;
        let i = 0;

        while (i < this.assemblyAttendances.length) {
            // Get the current tenant id
            const currentTenant = this.assemblyAttendances[i].tenantid;
            let groupCount = 0;

            // Count consecutive items with the same tenant id
            while (i < this.assemblyAttendances.length && this.assemblyAttendances[i].tenantid === currentTenant) {
                groupCount++;
                i++;
            }

            // If adding the entire group would exceed our max, stop
            if (count + groupCount > maxItems) {
                break;
            }

            count += groupCount;
        }

        return count;
    }

    async generateDownloadAttendanceList(assemblyid: string) {
        this.setIsAttendancePDFDownloading(true);
        await this.generateAttendanceListPdf(assemblyid);
        await this.downloadAttendanceListPdf();
        this.setIsAttendancePDFDownloading(false);
    }

    generateAttendanceListPdf = async (assemblyId: string) => {
        try {
            const accessToken = this.rootStore.authStore.token;
            const tokenType = this.rootStore.authStore.tokenType;
            const role = this.rootStore.authStore.user?.role;
            const response = await fetch(NetworkConfig.generateattendancelistpdf + assemblyId, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `${tokenType} ${accessToken}`,
                    "x-hasura-role": getRoleKey(role)
                }
            });
            if (response.status === 200) {
                const result = await response.json();
                this.setAttendanceFileId(JSON.parse(result.data));
            } else {
                this.setAttendanceFileId("");
                console.error("Error generateattendancelistpdf: ", response);
            }
        } catch (error) {
            this.setAttendanceFileId("");
            console.error("Error while trying to call generateattendancelistpdf: ", error);
        }
    };

    downloadAttendanceListPdf = async () => {
        const accessToken = this.rootStore.authStore.token;
        const tokenType = "Bearer";
        const role = this.rootStore.authStore.user?.role;
        try {
            const fetchResult = await fetch(`${NetworkConfig.downloadFileVersion2}/${this.attendanceFileId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `${tokenType} ${accessToken}`,
                    "x-hasura-role": getRoleKey(role)
                }
            });
            if (fetchResult.status === 200) {
                try {
                    const assemblyDate = this.rootStore.assemblyStore.currentAssembly?.date;
                    const date = assemblyDate ? format(new Date(assemblyDate), "dd_MM_yyyy") : "";
                    const title = this.rootStore.assemblyStore.currentAssembly?.title?.replace(/\s+/g, "_") || "";
                    const fileName = `Präsenzliste_${title}_am_${date}`;
                    // Start download
                    const fileBlob = await fetchResult.blob();
                    const objUrl = window.URL.createObjectURL(fileBlob);
                    const a = document.createElement("a");
                    a.href = objUrl;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                } catch (error) {
                    this.rootStore.uiStore.printStatusMessage(
                        i18next.t("screens.realestate.assembly.error.assembly_file_download_error"),
                        MessageType.ERROR
                    );
                }
            } else {
                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.realestate.assembly.error.assembly_file_download_error"),
                    MessageType.ERROR
                );
            }
        } catch (fetchError) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.realestate.assembly.error.assembly_file_download_error"),
                MessageType.ERROR
            );
        }
    };

    /* SETTERS */

    setSelectedRealestateId(selectedRealestateId: string) {
        this.selectedRealestateId = selectedRealestateId;
    }

    setAssemblyAttendances = (assemblyAttendances: IAssemblyAttendances[]) => {
        this.assemblyAttendances = assemblyAttendances;
    };

    setOriginalAssemblyAttendances = (assemblyAttendances: IAssemblyAttendances[]) => {
        this.originalAssemblyAttendances = assemblyAttendances;
    };

    setAttendanceFileId = (attendanceFileId: string) => {
        this.attendanceFileId = attendanceFileId;
    };

    setIsAttendancePDFDownloading = (isAttendancePDFDownloading: boolean) => {
        this.isAttendancePDFDownloading = isAttendancePDFDownloading;
    };

    setSearchQuery = (searchQuery: string) => {
        this.searchQuery = searchQuery;
    };
}
